import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ErrorState, Field, Spinner } from '../../components/ui.jsx';
import { api, ApiError } from '../../api/client.js';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { useMeta } from '../../state/MetaContext.jsx';
import { useToast } from '../../state/ToastContext.jsx';
import { formatXaf } from '../../lib/format.js';

const todayIso = () => new Date().toISOString().slice(0, 10);

const LeaseEditor = () => {
  useTitle('New lease');
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { billingPeriods } = useMeta();

  const { data, loading, error, reload } = useFetch('/tenancies/new/options');

  const [form, setForm] = useState({
    propertyId: params.get('propertyId') || '',
    unitId: params.get('unitId') || '',
    tenantId: params.get('tenantId') || '',
    applicationId: '',
    startDate: todayIso(),
    endDate: '',
    rent: '',
    deposit: '',
    internet: '',
    billingPeriod: 'monthly',
    rentDueDay: 1,
    specialConditions: '',
    terms: ''
  });
  const [includeInternet, setIncludeInternet] = useState(false);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [editTerms, setEditTerms] = useState(false);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  const property = useMemo(
    () => data?.properties.find((item) => String(item.id) === String(form.propertyId)),
    [data, form.propertyId]
  );

  const unitsHere = useMemo(
    () => (data?.units || []).filter((unit) => String(unit.propertyId) === String(form.propertyId)),
    [data, form.propertyId]
  );

  const unit = unitsHere.find((item) => String(item.id) === String(form.unitId));

  // Choosing a property or unit fills in the rent and deposit that were advertised,
  // which is nearly always what the lease should say.
  useEffect(() => {
    if (unit) {
      set({ rent: String(unit.rent), deposit: String(unit.rent * unit.depositMonths) });
    } else if (property && !form.unitId) {
      set({ rent: String(property.rent), deposit: String(property.rent * property.depositMonths) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.propertyId, form.unitId]);

  const applyApplication = (applicationId) => {
    const application = data.approvedApplications.find((item) => String(item.id) === String(applicationId));
    if (!application) {
      set({ applicationId: '' });
      return;
    }
    set({
      applicationId,
      propertyId: String(application.propertyId),
      unitId: application.unitId ? String(application.unitId) : '',
      tenantId: String(application.tenantId),
      startDate: application.moveInDate || todayIso(),
      rent: String(application.rent),
      deposit: String(application.rent * application.depositMonths)
    });
  };

  // Coming straight from Approve on the applications page. Fill the form from that
  // application once the options have loaded, so the landlord does not pick the
  // property and the person a second time. Runs once.
  const fromApplication = params.get('applicationId') || '';
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    if (!data || !fromApplication || prefilled) return;
    setPrefilled(true);

    const known = data.approvedApplications.some((item) => String(item.id) === String(fromApplication));
    if (!known) {
      // Already turned into a lease, or withdrawn since the button was rendered.
      toast.error('That application no longer needs a lease. Choose the property and tenant below.');
      return;
    }
    applyApplication(fromApplication);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, fromApplication, prefilled]);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});

    if (includeInternet && !Number(form.internet)) {
      setErrors({ internet: 'Choose the monthly internet amount.' });
      setBusy(false);
      return;
    }

    try {
      const result = await api.post('/tenancies', {
        propertyId: Number(form.propertyId),
        unitId: form.unitId ? Number(form.unitId) : null,
        tenantId: Number(form.tenantId),
        applicationId: form.applicationId ? Number(form.applicationId) : null,
        startDate: form.startDate,
        endDate: form.endDate || null,
        rent: Number(form.rent),
        deposit: Number(form.deposit || 0),
        internet: includeInternet ? Number(form.internet || 0) : 0,
        billingPeriod: form.billingPeriod,
        rentDueDay: Number(form.rentDueDay),
        terms: editTerms && form.terms.trim() ? form.terms : undefined,
        specialConditions: form.specialConditions.trim() || null
      });

      toast.success('Lease drafted. Read it through, then send it for signature.');
      navigate(`/landlord/leases/${result.tenancy.id}`);
    } catch (requestError) {
      if (requestError instanceof ApiError && requestError.details) {
        setErrors(requestError.details);
        toast.error('Some fields need attention.');
      } else {
        toast.error(requestError.message);
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Spinner />;
  if (error) {
    return (
      <div className="container section">
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  }

  const rent = Number(form.rent) || 0;
  const deposit = Number(form.deposit) || 0;
  const internet = Number(form.internet) || 0;
  const noProperties = data.properties.length === 0;
  const noTenants = data.tenants.length === 0;

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>Draw up a lease</h1>
          <p className="muted">
            The standard agreement is filled in from what you enter here. You can edit any clause before sending it out.
          </p>
        </div>
        <Link to="/landlord/leases" className="btn btn-ghost">
          Back to leases
        </Link>
      </header>

      {noProperties ? (
        <div className="notice notice-warn">
          You have no properties yet. <Link to="/landlord/properties/new">Add one first</Link>.
        </div>
      ) : null}

      {noTenants ? (
        <div className="notice notice-warn">
          You have no tenants on file. <Link to="/landlord/tenants">Add the tenant</Link> before drawing up their lease.
        </div>
      ) : null}

      <form className="editor-layout" onSubmit={submit}>
        <div className="editor-main">
          {data.approvedApplications.length > 0 ? (
            <section className="panel">
              <h2>Start from an approved application</h2>
              <p className="field-hint">
                These people applied through the marketplace and you approved them. Picking one fills in the rest.
              </p>
              <Field label="Approved applicant">
                <select value={form.applicationId} onChange={(event) => applyApplication(event.target.value)}>
                  <option value="">Not from an application</option>
                  {data.approvedApplications.map((application) => (
                    <option key={application.id} value={application.id}>
                      {application.tenantName} for {application.propertyTitle}
                    </option>
                  ))}
                </select>
              </Field>
            </section>
          ) : null}

          <section className="panel">
            <h2>Who and what</h2>

            <div className="grid-2">
              <Field label="Property" error={errors.propertyId} required>
                <select
                  value={form.propertyId}
                  onChange={(event) => set({ propertyId: event.target.value, unitId: '' })}
                  required
                >
                  <option value="">Choose a property</option>
                  {data.properties.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.title} ({item.city})
                    </option>
                  ))}
                </select>
              </Field>

              <Field
                label="Unit"
                hint={
                  unitsHere.length === 0
                    ? 'This property has no units, so the whole place is being let.'
                    : 'Which part of the building.'
                }
                error={errors.unitId}
              >
                <select
                  value={form.unitId}
                  onChange={(event) => set({ unitId: event.target.value })}
                  disabled={unitsHere.length === 0}
                >
                  <option value="">The whole property</option>
                  {unitsHere.map((item) => (
                    <option key={item.id} value={item.id} disabled={!item.available}>
                      {item.label} - {formatXaf(item.rent)}
                      {item.available ? '' : ' (already let)'}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Tenant" error={errors.tenantId} required>
              <select value={form.tenantId} onChange={(event) => set({ tenantId: event.target.value })} required>
                <option value="">Choose a tenant</option>
                {data.tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name} - {tenant.phone}
                  </option>
                ))}
              </select>
            </Field>
            <p className="field-hint">
              Not on the list? <Link to="/landlord/tenants">Create their account first</Link>.
            </p>
          </section>

          <section className="panel">
            <h2>Term and rent</h2>

            <div className="grid-2">
              <Field label="Tenancy starts" error={errors.startDate} required>
                <input
                  type="date"
                  value={form.startDate}
                  onChange={(event) => set({ startDate: event.target.value })}
                  required
                />
              </Field>

              <Field
                label="Tenancy ends"
                hint="Leave empty for a rolling month-to-month tenancy."
                error={errors.endDate}
              >
                <input type="date" value={form.endDate} onChange={(event) => set({ endDate: event.target.value })} />
              </Field>
            </div>

            <div className="grid-3">
              <Field
                label={`Rent in FCFA (per ${form.billingPeriod === 'yearly' ? 'year' : form.billingPeriod === 'quarterly' ? 'quarter' : 'month'})`}
                error={errors.rent}
                required
              >
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.rent}
                  onChange={(event) => set({ rent: event.target.value })}
                  required
                />
              </Field>

              <Field label="Paid" error={errors.billingPeriod}>
                <select value={form.billingPeriod} onChange={(event) => set({ billingPeriod: event.target.value })}>
                  {billingPeriods.map((period) => (
                    <option key={period.slug} value={period.slug}>
                      {period.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Due on day" hint="Of the month rent falls due." error={errors.rentDueDay}>
                <input
                  type="number"
                  min="1"
                  max="28"
                  value={form.rentDueDay}
                  onChange={(event) => set({ rentDueDay: event.target.value })}
                />
              </Field>
            </div>

            <div className="grid-2">
              <Field
                label="Deposit in FCFA"
                hint="Usually two months of rent. Charged once, at the start."
                error={errors.deposit}
              >
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.deposit}
                  onChange={(event) => set({ deposit: event.target.value })}
                />
              </Field>

              <div>
                <label className="check">
                  <input
                    type="checkbox"
                    checked={includeInternet}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setIncludeInternet(checked);
                      if (!checked) set({ internet: '' });
                    }}
                  />
                  <span>Offer internet service to this tenant</span>
                </label>
                {includeInternet ? (
                  <Field
                    label="Internet monthly fee in FCFA"
                    hint="Billed separately from rent and due on the 27th of each month."
                    error={errors.internet}
                  >
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={form.internet}
                      onChange={(event) => set({ internet: event.target.value })}
                    />
                  </Field>
                ) : null}
              </div>
            </div>

            {rent > 0 ? (
              <p className="field-hint">
                The tenant needs {formatXaf(rent + deposit + internet)} to move in, and{' '}
                {formatXaf(rent + internet)}{' '}
                {billingPeriods.find((period) => period.slug === form.billingPeriod)?.label.toLowerCase()} after that
                {internet > 0 ? ` (${formatXaf(rent)} rent and ${formatXaf(internet)} internet)` : ''}.
              </p>
            ) : null}
          </section>

          <section className="panel">
            <h2>The agreement</h2>
            <p className="field-hint">
              The standard ten-clause agreement is used unless you write your own. It covers the term, rent, deposit,
              utilities, repairs, access and how disputes are settled, under Cameroon law.
            </p>

            <Field label="Anything specific to this tenancy" error={errors.specialConditions}>
              <textarea
                rows="3"
                value={form.specialConditions}
                onChange={(event) => set({ specialConditions: event.target.value })}
                placeholder="No commercial cooking in the flat. The generator is shared and fuel is split between the six flats."
              />
            </Field>

            <label className="check">
              <input
                type="checkbox"
                checked={editTerms}
                onChange={(event) => {
                  setEditTerms(event.target.checked);
                  if (event.target.checked && !form.terms) set({ terms: data.defaultTerms });
                }}
              />
              <span>Write my own agreement instead of the standard one</span>
            </label>

            {editTerms ? (
              <Field label="The full agreement" error={errors.terms}>
                <textarea
                  rows="18"
                  className="mono"
                  value={form.terms}
                  onChange={(event) => set({ terms: event.target.value })}
                />
              </Field>
            ) : null}
          </section>
        </div>

        <aside className="editor-side">
          <div className="panel sticky-panel">
            <h2>Summary</h2>

            <dl className="summary-list">
              <div>
                <dt>Property</dt>
                <dd>{property ? property.title : 'Not chosen'}</dd>
              </div>
              <div>
                <dt>Unit</dt>
                <dd>{unit ? unit.label : property ? 'The whole property' : '-'}</dd>
              </div>
              <div>
                <dt>Tenant</dt>
                <dd>{data.tenants.find((item) => String(item.id) === String(form.tenantId))?.name || 'Not chosen'}</dd>
              </div>
              <div>
                <dt>Rent</dt>
                <dd>{rent > 0 ? formatXaf(rent) : '-'}</dd>
              </div>
              <div>
                <dt>Deposit</dt>
                <dd>{deposit > 0 ? formatXaf(deposit) : 'None'}</dd>
              </div>
              <div>
                <dt>Internet</dt>
                <dd>{internet > 0 ? formatXaf(internet) : 'Not included'}</dd>
              </div>
              <div>
                <dt>Starts</dt>
                <dd>{form.startDate || '-'}</dd>
              </div>
            </dl>

            <button
              type="submit"
              className="btn btn-primary btn-block btn-lg"
              disabled={busy || noProperties || noTenants}
            >
              {busy ? 'Saving...' : 'Create the draft'}
            </button>

            <p className="field-hint">
              Nothing takes effect yet. You will read it through, sign it, and send it to the tenant to sign. The rent
              schedule is created only once both signatures are in.
            </p>
          </div>
        </aside>
      </form>
    </div>
  );
};

export default LeaseEditor;
